import React from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  Box,
  Checkbox,
  Collapse,
  FormControlLabel,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import {
  ORDERABLE_UNITS,
  UNIT_BY_KEY,
  LABEL_BY_KEY,
  sanitiseLabelOrder
} from '../pedigree-tool/pedigree/labelDefinitions';
import {
  SORT_BY,
  SORT_DIR,
  SORT_KEYS_FOR_UNIT,
  valueSortFor
} from '../pedigree-tool/pedigree/labelValueSort';
import {
  FILTERS_FOR_UNIT,
  FILTER_NONE,
  LIMIT_CHOICES,
  LIMIT_NONE,
  WITHIN_YEARS,
  activeFilterCount,
  hasValueFilter,
  valueFilterFor
} from '../pedigree-tool/pedigree/labelValueFilter';
import { useAutoTranslation } from 'src/hooks/useTranslate';

const TEAL = '#2BA9BF';
const TEAL_HOVER = '#238a9c';

// TGCLOUD-1663 / 1672 — the reorderable label list.
//
// Drag and drop is the target the ticket asks for, with up/down buttons kept
// alongside rather than as a fallback: they are the only way to reorder with a
// keyboard on a touch device, and they make the affordance obvious to a user
// who would not think to try dragging. @hello-pangea/dnd (already in the
// bundle, used by the pathway board) also gives keyboard reordering and screen
// reader announcements on the drag handle itself.
//
// Rows are the ORDERABLE UNITS, not the label keys: several keys share one
// rendered row (first + last name; the phenotype and genotype name/ID pairs)
// and five compose the Tests block, so a per-key list would offer the user
// moves the chart cannot honour.

const SORT_LABEL = {
  [SORT_BY.ENTRY]: 'Order entered',
  [SORT_BY.DATE]: 'Date',
  [SORT_BY.ALPHA]: 'Name (A-Z)'
};

// The trailing columns, shared by the header, the group rows and the part rows.
//
// Both row kinds end with the same [sort][style][move] tail against the same
// right edge, so the B/I pair lands at the same x in each. The one that carries
// the alignment is MOVE_COL_W: the title is `flex: 1` and swallows any slack to
// its left, so dropping the SORT_COL_W spacer would move nothing, while dropping
// the trailing MOVE_COL_W spacer slides a part row's B/I 52px right of the
// group's. Both spacers are flexShrink: 0, because the group row's counterparts
// are real controls that cannot shrink — without that the two columns drift
// apart as the dialog narrows (TGCLOUD-1761).
const SORT_COL_W = 158;
const STYLE_COL_W = 57;
const MOVE_COL_W = 48;

const STYLE_KINDS = ['bold', 'italic'];
const STYLE_GLYPH = { bold: 'B', italic: 'I' };
const STYLE_NAME = { bold: 'Bold', italic: 'Italic' };
// The button's accessible name, one sentence per style.
const STYLE_ACTION = { bold: 'Bold {{title}}', italic: 'Italic {{title}}' };

/**
 * The bold / italic pair, rendered identically for a group row and for each of
 * its parts — only the key set differs (TGCLOUD-1761). Sharing one component is
 * deliberate: the group-only behaviour this fixes came from the group row
 * owning the only implementation of these buttons.
 *
 * A group's button is tri-state over the keys it speaks for: filled when every
 * one carries the style, dashed-and-pale when only some do, plain when none.
 * Clicking a mixed or empty button turns the style ON for all of them; the only
 * thing that turns it off is a button that is already fully on.
 *
 * The caller passes only the VISIBLE keys for a group. Styling a hidden label
 * has no visible effect, and a part row's own button is disabled for exactly
 * that reason — so a group control that wrote through to hidden parts would
 * contradict the rule its own children follow, and leave a disabled part button
 * reading as pressed with no way to clear it. It would also persist
 * `{visible: false, bold: true}` into a saved preset, styling a label the user
 * never chose to style if they later switch it on.
 *
 * The mixed state carries a SHAPE cue (dashed border) as well as a colour one:
 * teal-on-white against grey-on-white is a weak contrast to hang a three-way
 * distinction on, and colour alone fails for anyone who cannot separate the two.
 *
 * `aria-pressed` takes `mixed` for exactly this, so the state needs no invented
 * attribute to be readable by a screen reader.
 */
const StyleToggles = ({ keys, labelsSet, title, disabled, onToggle }) => {
  const t = useAutoTranslation();
  return (
    <Box sx={{ display: 'flex', gap: 0.5 }}>
      {STYLE_KINDS.map((kind) => {
        const on = keys.filter(
          (k) => !!labelsSet?.formatting?.[k]?.[kind]
        ).length;
        const active = on > 0 && on === keys.length;
        const mixed = on > 0 && on < keys.length;
        return (
          <Tooltip
            key={kind}
            title={
              disabled
                ? ''
                : mixed
                ? t('{{value}} (some parts)', { value: STYLE_NAME[kind] })
                : t(STYLE_NAME[kind])
            }
            arrow
            placement="top"
          >
            <span style={{ display: 'inline-flex' }}>
              <IconButton
                size="small"
                aria-pressed={mixed ? 'mixed' : active}
                aria-label={t(STYLE_ACTION[kind], { title })}
                // Formatting a hidden label has no visible effect, so the
                // control follows the checkbox — same rule the sidebar used.
                disabled={disabled}
                onClick={() => onToggle(kind, !active)}
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: 1,
                  border: '1px solid',
                  borderStyle: mixed ? 'dashed' : 'solid',
                  borderColor: active || mixed ? TEAL : 'rgba(0,0,0,0.23)',
                  backgroundColor: active
                    ? TEAL
                    : mixed
                    ? 'rgba(43,169,191,0.14)'
                    : 'transparent',
                  color: active
                    ? '#fff'
                    : mixed
                    ? TEAL_HOVER
                    : 'text.secondary',
                  fontSize: 13,
                  fontWeight: kind === 'bold' ? 700 : 500,
                  fontStyle: kind === 'italic' ? 'italic' : 'normal',
                  lineHeight: 1,
                  // Hover DARKENS in every state. Reusing the plain hover for
                  // mixed made it paler than at rest, so the pointer read as
                  // "less selected".
                  '&:hover': {
                    backgroundColor: active
                      ? TEAL_HOVER
                      : mixed
                      ? 'rgba(43,169,191,0.26)'
                      : 'rgba(43,169,191,0.08)'
                  },
                  '&.Mui-disabled': { opacity: 0.4 }
                }}
              >
                {STYLE_GLYPH[kind]}
              </IconButton>
            </span>
          </Tooltip>
        );
      })}
    </Box>
  );
};

/**
 * One member of a grouped row, in the same columns as the group row above it.
 *
 * These used to be a horizontal strip of checkboxes, which had room for a tick
 * and nothing else — and that is how per-label bold/italic became a group-only
 * control (TGCLOUD-1761). One label per line gives each its own B/I, so a user
 * can bold the Carrier Status while its gene symbol and ID stay upright.
 *
 * No drag handle on purpose: the parts of a group are not independently
 * orderable. A unit is one rendered row or one stacked block, so the layout
 * engine has no per-key position to move.
 */
const PartRow = ({ labelKey, labelsSet, onToggleKey, onToggleFormat }) => {
  const t = useAutoTranslation();

  // The definition's title is ours; a key with no definition shows as itself.
  const title = LABEL_BY_KEY[labelKey]?.title
    ? t(LABEL_BY_KEY[labelKey].title)
    : labelKey;
  const visible = !!labelsSet?.[labelKey];
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        py: 0.125,
        pr: 1
      }}
    >
      {/* FormControlLabel, not a bare Checkbox + Typography: it renders a real
          <label>, so the part's NAME stays a click target. The horizontal strip
          this replaced got that from FormControlLabel for free, and dropping it
          would have quietly shrunk each part's hit area to the 28px box. */}
      <FormControlLabel
        sx={{ flex: 1, minWidth: 0, ml: 0, mr: 0 }}
        control={
          <Checkbox
            size="small"
            checked={visible}
            onChange={(e) => onToggleKey(labelKey, e.target.checked)}
            sx={{ p: 0.5, '&.Mui-checked': { color: TEAL } }}
            slotProps={{
              input: { 'aria-label': t('Show {{title}}', { title }) }
            }}
          />
        }
        label={
          <Typography
            sx={{
              fontSize: 14,
              color: visible ? 'text.primary' : 'text.disabled'
            }}
          >
            {title}
          </Typography>
        }
      />
      <Box sx={{ width: SORT_COL_W, flexShrink: 0 }} />
      <StyleToggles
        keys={[labelKey]}
        labelsSet={labelsSet}
        title={title}
        disabled={!visible}
        onToggle={(kind, next) => onToggleFormat([labelKey], kind, next)}
      />
      <Box sx={{ width: MOVE_COL_W, flexShrink: 0 }} />
    </Box>
  );
};

// One row: drag handle, visibility, title, B/I, and (multi-value only) sort.
const UnitRow = ({
  unit,
  labelsSet,
  dragHandleProps,
  isDragging,
  onToggleVisible,
  onToggleFormat,
  onSortChange,
  onFilterChange,
  onMove,
  onToggleKey,
  expanded,
  onExpand,
  canMoveUp,
  canMoveDown,
  hiddenKeys
}) => {
  const t = useAutoTranslation();

  const def = UNIT_BY_KEY[unit];
  const title = t(def.title);
  const keys = (def.keys || []).filter((k) => !hiddenKeys.includes(k));
  // A unit whose every key is hidden by the anonymous clamp has nothing to
  // offer — showing dead checkboxes is how a control comes to look broken.
  if (def.keys.length && !keys.length) return null;

  const visibleKeys = keys.filter((k) => !!labelsSet?.[k]);
  const onCount = visibleKeys.length;
  const anyVisible = onCount > 0;
  // Several keys share one rendered row, and they are independently
  // configurable — hiding the genotype ID while keeping its symbol is a normal
  // thing to want. The row checkbox is therefore a tri-state group control and
  // the parts live behind the expander; collapsing them into one all-or-nothing
  // tick is what the per-key panel this replaced would have lost.
  const partial = onCount > 0 && onCount < keys.length;
  // TGCLOUD-1792 — a single-key unit with a filter (Diagnoses, Consultation
  // Dates, External IDs) needs the expander too; it used to appear only where
  // there were several parts to choose between.
  const filterable = hasValueFilter(unit) && !!onFilterChange;
  const hasParts = keys.length > 1;
  const expandable = hasParts || filterable;
  const activeFilters = filterable ? activeFilterCount(labelsSet, unit) : 0;
  const sortable = SORT_KEYS_FOR_UNIT[unit];
  const sort = sortable ? valueSortFor(labelsSet, unit) : null;

  return (
    <Box data-va-anchor="pedigree.labels.list">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          py: 0.25,
          pl: 0.5,
          pr: 1,
          borderRadius: 1,
          backgroundColor: isDragging ? 'rgba(43,169,191,0.10)' : 'transparent',
          '&:hover .row-actions': { opacity: 1 }
        }}
      >
        {def.locked ? (
          <Tooltip
            title={
              unit === 'reproductive'
                ? t(
                    'Shown beneath a pregnancy symbol, in a fixed clinical order'
                  )
                : t('Shown with the symbol, always first')
            }
            arrow
            placement="top"
          >
            <Box
              sx={{
                width: 24,
                display: 'flex',
                justifyContent: 'center',
                color: 'text.disabled'
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 16 }} />
            </Box>
          </Tooltip>
        ) : (
          <Tooltip title={t('Drag to reorder')} arrow placement="top">
            <Box
              {...dragHandleProps}
              sx={{
                width: 24,
                display: 'flex',
                justifyContent: 'center',
                cursor: 'grab',
                color: 'text.disabled',
                '&:hover': { color: 'text.secondary' }
              }}
            >
              <DragIndicatorIcon sx={{ fontSize: 18 }} />
            </Box>
          </Tooltip>
        )}

        {keys.length ? (
          <Checkbox
            size="small"
            checked={anyVisible}
            indeterminate={partial}
            name={unit}
            onChange={(e) => onToggleVisible(unit, e.target.checked)}
            sx={{
              p: 0.5,
              '&.Mui-checked': { color: TEAL },
              '&.MuiCheckbox-indeterminate': { color: TEAL }
            }}
            slotProps={{
              input: { 'aria-label': t('Show {{title}}', { title }) }
            }}
          />
        ) : (
          <Box sx={{ width: 30 }} />
        )}

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5
          }}
        >
          <Typography
            sx={{
              fontSize: 14,
              color:
                anyVisible || !keys.length ? 'text.primary' : 'text.disabled'
            }}
          >
            {title}
          </Typography>
          {expandable && (
            <Tooltip
              // The expander now reveals parts, filters, or both, so it has to
              // say which — a tooltip promising "parts" on the Diagnoses row,
              // which has exactly one part and a filter, would be wrong.
              title={
                expanded
                  ? t('Hide these settings')
                  : hasParts && filterable
                  ? t(
                      'choose which parts of {{title}} show, limit which records it prints',
                      { title }
                    )
                  : hasParts
                  ? t('choose which parts of {{title}} show', { title })
                  : t('limit which records it prints')
              }
              arrow
              placement="top"
            >
              <IconButton
                size="small"
                onClick={() => onExpand(unit)}
                aria-label={t('Settings for {{title}}', { title })}
                aria-expanded={!!expanded}
                sx={{ width: 22, height: 22 }}
              >
                {expanded ? (
                  <ExpandLessIcon sx={{ fontSize: 15 }} />
                ) : (
                  <ExpandMoreIcon sx={{ fontSize: 15 }} />
                )}
              </IconButton>
            </Tooltip>
          )}
          {partial && !expanded && (
            <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
              {t('{{onCount}} of {{count}}', { onCount, count: keys.length })}
            </Typography>
          )}
          {/* A filter hides clinical records, so the collapsed row has to show
              that one is set — otherwise the only clue is the chart itself. */}
          {activeFilters > 0 && !expanded && (
            <Typography
              sx={{ fontSize: 11, color: TEAL_HOVER, fontWeight: 600 }}
            >
              {activeFilters === 1
                ? t('1 filter')
                : t('{{activeFilters}} filters', { activeFilters })}
            </Typography>
          )}
        </Box>

        {sortable && (
          <TextField
            select
            size="small"
            value={sort.by}
            onChange={(e) => onSortChange(unit, { by: e.target.value })}
            disabled={!anyVisible}
            sx={{
              width: 132,
              '& .MuiInputBase-input': { fontSize: 12, py: 0.5 }
            }}
            slotProps={{
              htmlInput: {
                'aria-label': t('Sort {{title}} by', { title })
              },
              select: {
                'aria-label': t('Sort {{title}} by', { title })
              }
            }}
          >
            {sortable.map((by) => (
              <MenuItem key={by} value={by} sx={{ fontSize: 12 }}>
                {t(SORT_LABEL[by])}
              </MenuItem>
            ))}
          </TextField>
        )}
        {sortable ? (
          <Tooltip
            title={sort.dir === SORT_DIR.ASC ? t('Ascending') : t('Descending')}
            arrow
            placement="top"
          >
            <span style={{ display: 'inline-flex' }}>
              <IconButton
                size="small"
                disabled={!anyVisible}
                aria-label={t('Reverse {{title}} order', { title })}
                onClick={() =>
                  onSortChange(unit, {
                    dir:
                      sort.dir === SORT_DIR.ASC ? SORT_DIR.DESC : SORT_DIR.ASC
                  })
                }
                sx={{ width: 26, height: 26 }}
              >
                {sort.dir === SORT_DIR.ASC ? (
                  <ArrowUpwardOutlinedIcon sx={{ fontSize: 15 }} />
                ) : (
                  <ArrowDownwardOutlinedIcon sx={{ fontSize: 15 }} />
                )}
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <Box sx={{ width: SORT_COL_W }} />
        )}

        {keys.length ? (
          <StyleToggles
            keys={visibleKeys}
            labelsSet={labelsSet}
            title={title}
            disabled={!anyVisible}
            onToggle={(kind, next) => onToggleFormat(visibleKeys, kind, next)}
          />
        ) : (
          <Box sx={{ width: STYLE_COL_W }} />
        )}

        {def.locked ? (
          // No reorder controls at all, not disabled ones: a disabled button
          // reads as "movable, but not right now", and these two never move.
          <Box sx={{ width: MOVE_COL_W }} />
        ) : (
          <Box
            className="row-actions"
            sx={{
              display: 'flex',
              opacity: 0,
              transition: 'opacity 120ms',
              '&:focus-within': { opacity: 1 }
            }}
          >
            <IconButton
              size="small"
              disabled={!canMoveUp}
              aria-label={t('Move {{title}} up', { title })}
              onClick={() => onMove(unit, -1)}
              sx={{ width: 24, height: 24 }}
            >
              <ArrowUpwardIcon sx={{ fontSize: 14 }} />
            </IconButton>
            <IconButton
              size="small"
              disabled={!canMoveDown}
              aria-label={t('Move {{title}} down', { title })}
              onClick={() => onMove(unit, 1)}
              sx={{ width: 24, height: 24 }}
            >
              <ArrowDownwardIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* unmountOnExit: a collapsed Collapse keeps its children in the DOM, so
          the hidden part-checkboxes would stay focusable and reachable by a
          screen reader while invisible. */}
      {expandable && (
        <Collapse in={!!expanded} unmountOnExit>
          {/* Indented behind a teal rail, so six reproductive part rows still
              read as belonging to the group they sit under. */}
          <Box
            sx={{
              ml: 4,
              pl: 1,
              mb: 0.5,
              borderLeft: '2px solid',
              borderColor: 'rgba(43,169,191,0.35)'
            }}
          >
            {hasParts &&
              keys.map((k) => (
                <PartRow
                  key={k}
                  labelKey={k}
                  labelsSet={labelsSet}
                  onToggleKey={(key, checked) =>
                    onToggleKey(unit, key, checked)
                  }
                  onToggleFormat={onToggleFormat}
                />
              ))}
            {filterable && (
              <FilterPanel
                unit={unit}
                title={title}
                labelsSet={labelsSet}
                // Filtering a hidden label has no visible effect — the same rule
                // the B/I buttons follow.
                disabled={!anyVisible}
                onFilterChange={onFilterChange}
              />
            )}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

/**
 * TGCLOUD-1792 — the per-unit filter panel, inside the row's expander.
 *
 * It goes in the expander rather than in a new trailing column on purpose. The
 * row's tail is already [sort][style][move], and SORT_COL_W / STYLE_COL_W /
 * MOVE_COL_W are the ONLY thing aligning a part row's B/I under its group's —
 * via empty flexShrink:0 spacers. A fourth column means a fourth spacer in two
 * more places, and jsdom has no layout so no test can catch the drift. The
 * expander already exists for multi-key units; units with a filter now get one
 * too, and both kinds of per-unit detail live behind the same one affordance.
 *
 * The wording carries the two safety rules the filter model enforces, because a
 * clinician has to be able to trust what a filtered chart is claiming:
 *   • a record with no usable date is never hidden by the recency window, and
 *   • a record that does not state confirmation is never hidden by
 *     "confirmed only".
 * Both are stated in the panel, not just in the code.
 */
const FilterPanel = ({ unit, title, labelsSet, disabled, onFilterChange }) => {
  const t = useAutoTranslation();

  const allowed = FILTERS_FOR_UNIT[unit] || [];
  if (!allowed.length) return null;
  const f = valueFilterFor(labelsSet, unit);
  const field = {
    select: true,
    size: 'small',
    disabled,
    sx: { width: 150, '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, py: 0.5 }}>
      {allowed.includes('limit') && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            sx={{ fontSize: 12, width: 130, color: 'text.secondary' }}
          >
            {t('Show at most')}
          </Typography>
          <TextField
            {...field}
            value={f.limit}
            onChange={(e) =>
              onFilterChange(unit, { limit: Number(e.target.value) })
            }
            slotProps={{
              htmlInput: { 'aria-label': t('Limit {{title}} rows', { title }) },
              select: { 'aria-label': t('Limit {{title}} rows', { title }) }
            }}
          >
            <MenuItem value={LIMIT_NONE} sx={{ fontSize: 12 }}>
              {t('All')}
            </MenuItem>
            {LIMIT_CHOICES.map((n) => (
              <MenuItem key={n} value={n} sx={{ fontSize: 12 }}>
                {n}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      )}

      {allowed.includes('within') && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            sx={{ fontSize: 12, width: 130, color: 'text.secondary' }}
          >
            {t('Recorded within')}
          </Typography>
          <TextField
            {...field}
            value={f.within}
            onChange={(e) => {
              const raw = e.target.value;
              onFilterChange(unit, {
                within: raw === FILTER_NONE ? FILTER_NONE : Number(raw)
              });
            }}
            slotProps={{
              htmlInput: {
                'aria-label': t('Limit {{title}} by date', { title })
              },
              select: { 'aria-label': t('Limit {{title}} by date', { title }) }
            }}
          >
            <MenuItem value={FILTER_NONE} sx={{ fontSize: 12 }}>
              {t('Any date')}
            </MenuItem>
            {WITHIN_YEARS.map((y) => (
              <MenuItem key={y} value={y} sx={{ fontSize: 12 }}>
                {y === 1 ? t('Last year') : t('Last {{y}} years', { y })}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      )}

      {allowed.includes('confirmedOnly') && (
        <FormControlLabel
          sx={{ ml: 0 }}
          control={
            <Checkbox
              size="small"
              checked={!!f.confirmedOnly}
              disabled={disabled}
              onChange={(e) =>
                onFilterChange(unit, { confirmedOnly: e.target.checked })
              }
              sx={{ p: 0.5, '&.Mui-checked': { color: TEAL } }}
              slotProps={{
                input: {
                  'aria-label': t('Only clinically confirmed {{title}}', {
                    title
                  })
                }
              }}
            />
          }
          label={
            <Typography sx={{ fontSize: 12 }}>
              {t('Clinically confirmed only')}
            </Typography>
          }
        />
      )}

      <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
        {t(
          'Records that do not state a date or a confirmation are always shown. A person with more records than are shown gets a “+ N more” line, on screen and in an export.'
        )}
      </Typography>
    </Box>
  );
};

/**
 * Reorder a unit list by moving one unit to a new position, then re-sanitise so
 * the locked units land back in their own slots. Exported for the tests: this
 * is the whole ordering mutation, and it is worth asserting without a DOM.
 */
export const moveUnit = (order, unit, toIndex) => {
  const draggable = sanitiseLabelOrder(order).filter(
    (u) => !UNIT_BY_KEY[u].locked
  );
  const from = draggable.indexOf(unit);
  if (from === -1) return sanitiseLabelOrder(order);
  const clamped = Math.max(0, Math.min(draggable.length - 1, toIndex));
  draggable.splice(from, 1);
  draggable.splice(clamped, 0, unit);
  return sanitiseLabelOrder(draggable);
};

const LabelOrderList = ({
  labelsSet,
  order,
  hiddenKeys = [],
  onOrderChange,
  onToggleVisible,
  onToggleKey,
  onToggleFormat,
  onSortChange,
  onFilterChange
}) => {
  const t = useAutoTranslation();

  // One row open at a time: the list is long enough that expanding several
  // pushes the save controls off screen.
  const [expandedUnit, setExpandedUnit] = React.useState(null);
  const handleExpand = (unit) =>
    setExpandedUnit((prev) => (prev === unit ? null : unit));
  const safeOrder = sanitiseLabelOrder(order);
  // The drag list is the movable subset; locked units render in place but are
  // not Draggables, so the library never has to reason about immovable items.
  const draggable = safeOrder.filter((u) => !UNIT_BY_KEY[u].locked);

  const handleDragEnd = (result) => {
    if (!result?.destination) return;
    const unit = result.draggableId;
    if (result.destination.index === result.source.index) return;
    onOrderChange(moveUnit(safeOrder, unit, result.destination.index));
  };

  const handleMove = (unit, delta) => {
    const at = draggable.indexOf(unit);
    if (at === -1) return;
    onOrderChange(moveUnit(safeOrder, unit, at + delta));
  };

  const rowProps = {
    labelsSet,
    onToggleVisible,
    onToggleKey,
    onToggleFormat,
    onSortChange,
    onFilterChange,
    onMove: handleMove,
    onExpand: handleExpand,
    hiddenKeys
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 0.5,
          pl: 7.5,
          pr: 1
        }}
      >
        <Typography
          sx={{ fontSize: 11, color: 'text.disabled', flex: 1, minWidth: 0 }}
        >
          {t('Drag to set the order labels appear under each person')}
        </Typography>
        <Typography
          sx={{ fontSize: 11, color: 'text.disabled', width: SORT_COL_W }}
        >
          {t('Order of values')}
        </Typography>
        <Typography
          sx={{ fontSize: 11, color: 'text.disabled', width: STYLE_COL_W }}
        >
          {t('Style')}
        </Typography>
        <Box sx={{ width: MOVE_COL_W }} />
      </Box>

      <DragDropContext
        onDragEnd={handleDragEnd}
        // The library's own screen-reader instructions, in its own words.
        dragHandleUsageInstructions={t(
          'Press space bar to start a drag. When dragging you can use the arrow keys to move the item around and escape to cancel. Some screen readers may require you to be in focus mode or to use your pass through key'
        )}
      >
        <Droppable droppableId="pedigree-label-order">
          {(dropProvided) => (
            <Box
              ref={dropProvided.innerRef}
              {...dropProvided.droppableProps}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 0.5
              }}
            >
              {safeOrder.map((unit) => {
                if (UNIT_BY_KEY[unit].locked) {
                  return (
                    <UnitRow
                      key={unit}
                      unit={unit}
                      {...rowProps}
                      expanded={expandedUnit === unit}
                      canMoveUp={false}
                      canMoveDown={false}
                    />
                  );
                }
                const index = draggable.indexOf(unit);
                return (
                  <Draggable key={unit} draggableId={unit} index={index}>
                    {(dragProvided, snapshot) => (
                      <Box
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                      >
                        <UnitRow
                          unit={unit}
                          {...rowProps}
                          expanded={expandedUnit === unit}
                          dragHandleProps={dragProvided.dragHandleProps}
                          isDragging={snapshot.isDragging}
                          canMoveUp={index > 0}
                          canMoveDown={index < draggable.length - 1}
                        />
                      </Box>
                    )}
                  </Draggable>
                );
              })}
              {dropProvided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>
    </Box>
  );
};

export { ORDERABLE_UNITS };
export default LabelOrderList;
